/* eslint-disable no-plusplus */
/* eslint-disable no-multiple-empty-lines */
// Packages
import Big from 'big.js'
import moment from 'moment-timezone'
import validator from 'validator'
// Helpers
import {
  InvoiceV2,
  ReserveV2,
  ReserveAccountV2,
} from '../api-v2'
import { CError, CLog } from '../consoleMixin'
import {
  configureUrlString,
  setSearchURLAndLocalStorage,
} from '../searchHelpers'
import { ClientMapper, ReservesMapper, mapActivitiesToInvoiceUpdates, mapBrokerPaymentsV2ResponseToV1Response, mapTagsToFlags } from './mappings'
import TagMappings from '../../shared/TagMappings.json'
import _Store from '../../store/store'

/**
 * @typedef {import('../api-v2/js-doc').V2InvoiceResponse} V2InvoiceResponse
 * @typedef {import('../api-v2/js-doc').UiInvoiceModel} UiInvoiceModel
 */

const brokerPaidStatuses = [ 'shortpaid', 'overpaid', 'nonpayment', 'in_full' ]

export const findTag = (invoiceUpdate) => {
  return Object
    .keys(TagMappings)
    .find((key) => {
      return TagMappings[key].includes((invoiceUpdate))
    })
}

export const buildFilter = (key, value, operator = '$eq') => {
  if (Array.isArray(value)) {
    return `${key}:${operator}:[${value.join(',')}]`
  }
  return `${key}:${operator}:[${value}]`
}

const buildKeyValueOperatorParam = (key, value, operator = '$eq') => {
  return {
    key,
    value,
    operator,
  }
}

const notPassedVerificationStatuses = 'required,in_progress'
const passedVerificationStatuses = 'not_required,bypassed,verified'
const unallowedCleanInvoicesTagGroups='INVOICE_ISSUES,REJECTION_REASONS,BROKER_CONFIGURATION,PROCESSING_ACTION_ITEMS,ISSUES_SENDING_INVOICE_TO_BROKER'

export const buildQueryFilter = (params)  =>{
  const filters = []
  for (const param of params) {
    filters.push(buildFilter(param.key, param.value, param.operator))
  }
  // TODO: if buildFilter always returns a value (or error), what purpose does applicableFilters serve?
  const applicableFilters = filters.filter((item) => {
    return item != null
  })
  return applicableFilters
}

export const buildSortParams = (sortParams)  =>{
  return Object
    .entries(sortParams)
    .map(([ key, value ]) => {
      return `${key}:${value}`
    })
}

const _buildStatusFilter = (status) => {
  switch (status) {
    case 'pending': return 'under_review'
    case 'approved':
    case 'paid': return 'purchased'
    case 'declined' : return 'rejected'
    default:
      return 'under_review'
  }
}



const getFullDocumentUrl = (documents) => {
  const combined = documents.find((doc) => {
    return doc.type === 'generated'
  })
  return combined
    ? combined.internalUrl
    : null
}

const isInvoicePaidByBroker = (invoice) => {
  return brokerPaidStatuses.includes(invoice.brokerPaymentStatus)
}

// v2 broker = v1 debtor
const mapBroker = (broker) => {
  // eslint-disable-next-line arrow-body-style
  const officeAddress = broker?.addresses?.find(item => item.type === 'office')
  // eslint-disable-next-line arrow-body-style
  const mailingAddress = broker?.addresses?.find(item => item.type === 'mailing')
  return {
    ...broker,
    address: officeAddress?.address,
    address_2: officeAddress?.streetAddress,
    city: officeAddress?.city,
    country: officeAddress?.country,
    state: officeAddress?.state,
    zip: officeAddress?.zip,
    mailing_address: mailingAddress?.address,
    mailing_address_2: mailingAddress?.streetAddress,
    mailing_city: mailingAddress?.city,
    mailing_country: mailingAddress?.country,
    mailing_state: mailingAddress?.state,
    mailing_zip: mailingAddress?.zip,
    display_rating: broker.displayRating,
    external_rating: broker.externalRating,
    name: broker.legalName,
    doing_business_as: [ broker.doingBusinessAs ],
    invoice_delivery_emails: broker.emails
      .filter((email) => {
        return email.type === 'invoice_delivery'
      })
      .map((email) => {
        return email.email
      }),
    noa_emails: broker.emails
      .filter((email) => {
        return email.type === 'NOA'
      })
      .map((email) => {
        return email.email
      }),
    pay_status_emails: broker.emails
      .filter((email) => {
        return email.type === 'payment_status'
      })
      .map((email) => {
        return email.email
      }),
    require_copies: broker.tags.some((tag) => {
      return tag.key === 'BROKER_REQUIRE_COPIES'
    }),
    require_email: broker.tags.some((tag) => {
      return tag.key === 'BROKER_REQUIRE_EMAIL'
    }),
    require_fax: broker.tags.some((tag) => {
      return tag.key === 'BROKER_REQUIRE_FAX'
    }),
    require_online_submit: broker.tags.some((tag) => {
      return tag.key === 'BROKER_REQUIRE_ONLINE_SUBMIT'
    }),
    require_originals: broker.tags.some((tag) => {
      return tag.key === 'BROKER_REQUIRE_ORIGINALS'
    }),
  }
}

const mapDocumentType = (document) => {
  if (document.type === 'generated') {
    return 'combined'
  }
  else if (document.type === 'uploaded') {
    return 'normal'
  }
  else {
    CError(`Document type ${document.type} can't be mapped from v2 to v1`)
  }
}

const mapInvoiceBrokerPaymentStatus = (invoice) => {
  switch (invoice.brokerPaymentStatus) {
    case 'in_full':
      return 'fully_paid'
    case 'non_factored_payment':
      return 'pending_payment'
    case 'not_received':
      return 'pending_payment'
    case 'nonpayment':
      return 'nonpayment'
    case 'overpaid':
      return 'overpay'
    case 'shortpaid':
      return 'shortpay'

    default:
      CError(`Invoice broker payment status ${invoice.brokerPaymentStatus} can't be mapped from v2 to v1`)
      return invoice.clientPaymentStatus
  }
}

const mapInvoiceClientPaymentStatus = (invoice) => {
  switch (invoice.clientPaymentStatus) {
    case 'completed':
      return 'confirmed'
    case 'failed':
      return 'confirmed'
    case 'in_progress':
      return 'processing'
    case 'not_applicable':
      return 'not_paid'
    case 'pending':
      return 'not_paid'
    case 'sent':
      return 'confirmed'

    default:
      CError(`Invoice client payment status ${invoice.clientPaymentStatus} can't be mapped from v2 to v1`)
      return invoice.clientPaymentStatus
  }
}

const mapInvoiceDocuments = (document, id) => {
  return {
    ...document,
    created_at: document.createdAt,
    document_type: mapDocumentType(document),
    invoice_id: id,
    is_invalid: false,
    name: document.name,
    updated_at: document.updatedAt,
    updated_by: document.updatedBy,
    url: document.internalUrl,
  }
}

const mapInvoiceStatus = (invoice) => {
  if (invoice.status === 'under_review') {
    return 'pending'
  }
  else if (invoice.status === 'purchased' && !isInvoicePaidByBroker(invoice)) {
    return 'approved'
  }
  else if (invoice.status === 'purchased' && isInvoicePaidByBroker(invoice)) {
    return 'paid'
  }
  else if (invoice.status === 'rejected') {
    return 'declined'
  }
  else {
    CError(`Invoice status ${invoice.status} can't be mapped from v2 to v1`)
  }
}

export const mapV2InvoiceRequestResponseProperties = (invoicesResult) => {
  return {
    count: invoicesResult.pagination.totalItems,
    facets_stats: {
      total_amount: {
        sum : invoicesResult.additionalAttributes.totalAmount,
      },
      client_total_by_id: invoicesResult.additionalAttributes.clientTotalGrouppedById,
      debtor_total_by_id: invoicesResult.additionalAttributes.brokerTotalGrouppedById,
    },
    pages: invoicesResult.pagination.totalPages,
    rows: invoicesResult.items.map((item) => {
      return transformV2RawInvoice(item)
    }),
  }
}

export const mapV2ResponseAmountToV1ResponseAmount = (amount) => {
  return Number(Math.abs(amount))
}

export const searchV2Invoices = async (status, data, props) => {
  let displayId = ''
  let invoiceIds = null

  if (data.ids && !Array.isArray(data.ids) && validator.isNumeric(data.ids)) {
    // Used when you type a ID in the select search
    displayId = `${data.ids}`
  }

  else if (data.ids) {
    // Used when you click "view invoices" from an invoice update
    invoiceIds = Array.isArray(data.ids)
      ? data.ids
      : [ data.ids ]
  }

  const [ filterParams, sortParams, paginationParams ] = setV2Params({
    accountManagerID: data.inputs?.accountManager?.id,
    brokerID: data.inputs?.debtorID,
    brokerRequiresOnlineSubmit: data.apiValues?.requireOnlineSubmit,
    brokerRequiresOriginals: data.apiValues?.requireOriginals,
    brokerRequiresCopies: data.apiValues?.requireCopies,
    cleanInvoices: data.inputs?.general  === 'clean invoices',
    clientID: data.inputs?.clientID,
    displayId: {
      operator : data.id?.operator || '$ilike',
      value : data.inputs?.invoiceID || displayId,
    },
    flagged: data.apiValues?.flagged,
    gtDate: data.apiValues?.gtDate,
    id: {
      operator: data.id?.operator || '$in',
      value : data.inputs?.invoiceID || invoiceIds,
    },
    inactiveClients: data.inputs?.general  === 'inactive clients',
    limit: 25,
    loadNumber: {
      operator : data.loadNumber?.operator || '$eq',
      value : data.inputs?.load || data.loadNumber?.value,
    },
    ltDate: data.apiValues?.ltDate,
    nonpayment: data.apiValues?.nonpaid,
    overpay: data.apiValues?.overpay,
    page: props?.page || 1,
    predefined: data.inputs?.predefined,
    purchasedDateGt: data.inputs?.daysOutstandingTo,
    purchasedDateLt: data.inputs?.daysOutstandingFrom || data.inputs?.purchasedDateLt,
    shortpay: data.apiValues?.shortpay,
    sort: data.inputs?.sort,
    status,
    submittedStartDate: data.inputs?.submittedStartDate,
    submittedEndDate: data.inputs?.submittedEndDate,
    tags: {
      operator: '$in',
      value: data.inputs?.flag,
    },
    transferTime: data.apiValues?.transferTime,
    transferType: data.apiValues?.transferType,
    verificationStatus: {
      operator: '$in',
      value: data.apiValues?.requiresVerification
        ? notPassedVerificationStatuses
        : data.inputs?.general  === 'clean invoices'
          ? passedVerificationStatuses
          : '',
    },
    vipStatus: data.inputs?.vipStatus,
  })
  const invoices = (await new InvoiceV2().list(filterParams, sortParams, paginationParams)).data
  const invoicesIDs = invoices.items.map((invoice) => {
    return invoice.id
  })
  const urlString = configureUrlString(status, data, props)

  setSearchURLAndLocalStorage(
    status,
    // TODO: update to `invoices.all_invoice_ids`
    // This is needed for the debugObject in goToNextInvoice
    invoicesIDs,
    { status, data, props },
    urlString
  )

  if (invoices) {
    return mapV2InvoiceRequestResponseProperties(invoices)
  }
  else {
    CError(`Invoices from V2 were not loaded correctly for status ${status}`)
    return null
  }
}

export const searchV2Reserves = async (clientId, reason = null, page, limit) => {
  const filterParams = []
  const paginationParams = {}
  const sortParams = {}

  if (reason) {
    filterParams.push(buildKeyValueOperatorParam('reason', reason))
  }

  if (limit) {
    paginationParams.limit = limit
  }

  if (page) {
    paginationParams.page = page
  }

  const response = (await new ReserveV2(clientId).list(filterParams, sortParams, paginationParams)).data
  const mappedReserves = response.items.map((reserve) => {
    return ReservesMapper.v2ReserveToV1(reserve)
  })

  return {
    items: mappedReserves,
    pagination: response.pagination,
  }
}

export const searchV2ReserveAccount = async (clientId, page, limit) => {
  const paginationParams = {}

  if (limit) {
    paginationParams.limit = limit
  }

  if (page) {
    paginationParams.page = page
  }

  const response = (await new ReserveAccountV2(clientId).list(paginationParams)).data
  const items = response.items.map((reserve) => {
    return {
      ...reserve,
      notes: reserve.note,
    }
  })
  return {
    items,
    pagination: response.pagination,
  }
}

export const postV2ReserveAccount = async (clientId, payload) => {
  const response = (await new ReserveAccountV2(clientId).postReserveAccount(payload))
  return response
}

export const setV2Params = ({
  accountManagerID,
  cleanInvoices,
  clientID,
  brokerID,
  brokerRequiresOnlineSubmit,
  brokerRequiresOriginals,
  brokerRequiresCopies,
  flagged,
  gtDate,
  id = {
    operator: '$eq',
    value: '',
  },
  limit,
  loadNumber = {
    operator : '$eq',
    value : '',
  },
  displayId = {
    operator : '$eq',
    value : '',
  },
  ltDate,
  nonpayment,
  overpay,
  page,
  predefined,
  purchasedDateGt,
  purchasedDateLt,
  verificationStatus = {
    operator: '$in',
    value: '',
  },
  shortpay,
  sort,
  status,
  submittedEndDate,
  submittedStartDate,
  tags = {
    operator: '$in',
    value: '',
  },
  transferTime,
  transferType,
  vipStatus,
  inactiveClients,
}) => {
  const filterParams = []
  const paginationParams = {}
  const sortParams = {}

  if (limit) {
    paginationParams.limit = limit
  }

  if (page) {
    paginationParams.page = page
  }

  if (status) {
    filterParams.push(buildKeyValueOperatorParam('status', _buildStatusFilter(status)))

    if (status === 'pending') {
      if (sort === 'time') {
        sortParams.createdAt = 'ASC'
      }

      if (sort === 'client') {
        sortParams.clientUnderReviewTotal = 'DESC'
      }

      if (sort === 'debtor') {
        sortParams.brokerUnderReviewTotal = 'DESC'
      }

      if (flagged) {
        filterParams.push(buildKeyValueOperatorParam('tagGroups', unallowedCleanInvoicesTagGroups, '$in'))
      }

      if (cleanInvoices) {
        filterParams.push(buildKeyValueOperatorParam('tags', '', '$null'))
      }

      if (verificationStatus?.value) {
        filterParams.push(buildKeyValueOperatorParam('verificationStatus', verificationStatus.value, verificationStatus.operator))
      }

      if (transferTime && transferType === 'ach') {
        let transferFilterCriteria = 'first_ach'
        switch (transferTime) {
          case '1pm':
            transferFilterCriteria = 'first_ach'
            break

          case '10pm':
            transferFilterCriteria = 'second_ach'
            break
        }
        filterParams.push(buildKeyValueOperatorParam('transfer', transferFilterCriteria))
      }

      if (transferType === 'wire') {
        filterParams.push(buildKeyValueOperatorParam('transfer', 'expedited'))
      }
    }
    else if (status === 'declined') {
      sortParams.rejectedDate = 'DESC'

      if (gtDate) {
        filterParams.push(buildKeyValueOperatorParam('rejectedDate', gtDate.toISOString(), '$gt'))
      }

      if (ltDate) {
        filterParams.push(buildKeyValueOperatorParam('rejectedDate', ltDate.toISOString(), '$lt'))
      }
    }
    else {
      if (purchasedDateGt) {
        const outstandingGt = moment(purchasedDateLt).tz('America/New_York').startOf('day').toISOString()
        CLog('Setting outstanding filter to greater than', outstandingGt)
        filterParams.push(buildKeyValueOperatorParam(
          'outstanding',
          outstandingGt,
          '$gt'
        ))
      }

      if (purchasedDateLt) {
        const outstandingLt = moment(purchasedDateLt).tz('America/New_York').startOf('day').toISOString()
        CLog('Setting outstanding filter to less than', outstandingLt)
        filterParams.push(buildKeyValueOperatorParam(
          'outstanding',
          outstandingLt,
          '$lt'
        ))
      }

      if (status === 'approved') {
        filterParams.push(buildKeyValueOperatorParam('brokerPaymentStatus', brokerPaidStatuses, '$nin' ))

        if (sort === 'time') {
          sortParams.purchasedDate = 'ASC'
        }

        if (sort === 'total') {
          sortParams.value = 'DESC'
        }

        if (sort === 'client') {
          sortParams.clientPurchasedTotal = 'DESC'
        }

        if (sort === 'debtor') {
          sortParams.brokerPurchasedTotal = 'DESC'
        }

        if (brokerRequiresCopies) {
          filterParams.push(buildKeyValueOperatorParam('brokerTag','BROKER_REQUIRE_COPIES'))
        }

        if (brokerRequiresOnlineSubmit) {
          filterParams.push(buildKeyValueOperatorParam('brokerTag','BROKER_REQUIRE_ONLINE_SUBMIT'))
        }

        if (brokerRequiresOriginals) {
          filterParams.push(buildKeyValueOperatorParam('brokerTag','BROKER_REQUIRE_ORIGINALS'))
        }

        if (predefined === 'delayed payments') {
          const daysAgo14 = moment()
            .tz('America/New_York')
            .startOf('day')
            .subtract(14, 'days')
            .toISOString()
          filterParams.push(buildKeyValueOperatorParam('paymentDate', daysAgo14, '$gt'))
        }

        if (predefined === 'unopened emails') {
          // TODO: uncomment and update this line when v2 supports this filter
          // filterParams.push(buildKeyValueOperatorParam('unopenedEmails', true))
        }

        if (predefined === 'flagged') {
          filterParams.push(buildKeyValueOperatorParam('tags.tagDefinition.group.group.key', unallowedCleanInvoicesTagGroups, '$in'))
        }

        if (predefined === 'buyout') {
          filterParams.push(buildKeyValueOperatorParam('buyoutId', '', '$notnull'))
        }
      }

      if (status === 'paid') {
        sortParams.purchasedDate = 'DESC'

        const brokerPaymentStatusFilter = filterParams.find((param) => {
          return param.key === 'brokerPaymentStatus'
        })

        if (!brokerPaymentStatusFilter && !overpay && !shortpay && !nonpayment) {
          filterParams.push(buildKeyValueOperatorParam('brokerPaymentStatus', brokerPaidStatuses, '$in' ))
        }
        else if (overpay) {
          filterParams.push(buildKeyValueOperatorParam('brokerPaymentStatus', 'overpaid'))
        }
        else if (shortpay) {
          filterParams.push(buildKeyValueOperatorParam('brokerPaymentStatus', 'shortpaid'))
        }
        else if (nonpayment) {
          filterParams.push(buildKeyValueOperatorParam('brokerPaymentStatus', 'nonpayment'))
        }

        if (gtDate) {
          filterParams.push(buildKeyValueOperatorParam('purchasedDate', gtDate.toISOString(), '$gt'))
        }

        if (ltDate) {
          filterParams.push(buildKeyValueOperatorParam('purchasedDate', ltDate.toISOString(), '$lt'))
        }
      }
    }
  }

  if (accountManagerID) {
    filterParams.push(buildKeyValueOperatorParam('successTeamId', accountManagerID, '$eq'))
  }

  if (brokerID) {
    // Debtor not found
    if (brokerID === _Store.debtorNotFound.id) {
      filterParams.push(buildKeyValueOperatorParam('brokerId', '', '$null'))
    }
    else {
      filterParams.push(buildKeyValueOperatorParam('brokerId', brokerID))
    }
  }

  if (clientID) {
    filterParams.push(buildKeyValueOperatorParam('clientId', clientID))
  }

  if (displayId?.value) {
    filterParams.push(buildKeyValueOperatorParam('displayId', displayId.value, displayId.operator))
  }

  if (loadNumber?.value) {
    filterParams.push(buildKeyValueOperatorParam('loadNumber', loadNumber.value, loadNumber.operator))

    if (loadNumber.operator === '$ilike') {
      sortParams.createdAt = 'DESC'
    }
  }

  if (id?.value) {
    filterParams.push(buildKeyValueOperatorParam('id', id.value, id.operator))

    if (id.operator === '$ilike') {
      sortParams.createdAt = 'DESC'
    }
  }

  if (tags?.value) {
    let operator = tags.operator
    let tagFilterValue
    const separateTags = tags?.value.split(',').map((flag) => {
      return findTag(flag)
    })
    if (separateTags.length > 1) {
      operator = '$in'
      tagFilterValue = separateTags
    }
    else {
      operator = '$eq'
      tagFilterValue = separateTags[0]
    }
    filterParams.push(buildKeyValueOperatorParam('tags', tagFilterValue, operator))
  }

  if (vipStatus) {
    filterParams.push(buildKeyValueOperatorParam('vip', vipStatus))
  }

  if (inactiveClients) {
    filterParams.push(buildKeyValueOperatorParam('inactiveClients', inactiveClients))
  }

  if (submittedStartDate) {
    filterParams.push(buildKeyValueOperatorParam('createdAt', submittedStartDate, '$gte'))
  }

  if (submittedEndDate) {
    filterParams.push(buildKeyValueOperatorParam('createdAt', submittedEndDate, '$lte'))
  }

  return [ filterParams, sortParams, paginationParams ]
}

/**
 * @param {V2InvoiceResponse} invoice - V2 invoice response body
 * @returns {UiInvoiceModel}
 */
export const transformV2RawInvoice = (invoice) => {
  if (invoice.transformed) {
    return invoice
  }

  const daysApproved = (moment().startOf('day')).diff(moment(invoice.purchasedDate).startOf('day'), 'days')
  const invoiceMappedForV2 = {
    ...invoice,
    load_number: invoice.loadNumber,
    client_id: invoice.clientId,
    debtor_id: invoice.brokerId,
    created_at: invoice.createdAt,
    updated_at: invoice.updatedAt,
    updated_by: invoice.updatedBy,
    created_by: invoice.createdBy,
    display_id: invoice.displayId,
    client: ClientMapper.v2ClientToUI(invoice.client),
    debtor: invoice.broker
      ? mapBroker(invoice.broker)
      : _Store.debtorNotFound,
    debtor_payment: invoice.brokerPayments
      ? mapBrokerPaymentsV2ResponseToV1Response(invoice.brokerPayments)
      : null,
    debtor_selected_name : invoice.broker?.legalName
      ? invoice.broker?.legalName
      : _Store.debtorNotFound.debtor_selected_name,
    flagged: mapTagsToFlags(invoice).split(',').length > 0,
    full_document_url: getFullDocumentUrl(invoice.documents),
    has_invalid_documents: false,
    individual_invoice_documents : invoice.documents
      .map((document) => {
        return mapInvoiceDocuments(document, invoice.id)
      })
      .filter((document) => {
        return document.document_type === 'normal'
      }),
    invoice_documents : invoice.documents.map((document) => {
      return mapInvoiceDocuments(document, invoice.id)
    }),
    invoice_updates: mapActivitiesToInvoiceUpdates(invoice),
    last_flag_message: mapTagsToFlags(invoice),
    possible_duplicate: false,
    status: mapInvoiceStatus(invoice),
    status_client_payment: mapInvoiceClientPaymentStatus(invoice),
    status_debtor_payment: mapInvoiceBrokerPaymentStatus(invoice),
    total_amount: invoice.value,
    transfer_type: invoice.expedited
      ? 'wire'
      : 'ach',
    primary_rate: invoice.lineHaulRate,
  }
  const v2_approved_factor_fee = Big(invoiceMappedForV2.totalAmount)
    .mul(Big(invoiceMappedForV2.approvedFactorFee))
    .div(100)
  const client_total_to_pay = Big(invoiceMappedForV2.totalAmount).minus(v2_approved_factor_fee)
  const totalAmount = new Big(invoiceMappedForV2.totalAmount)
  let overpaid = Big(0)
  let shortpaid = Big(0)

  if (invoice.brokerPayments && invoice.brokerPayments.length) {
    const totalBrokerPaid = new Big(
      invoice.brokerPayments.reduce((acc, cur) => {
        return acc.plus(new Big(cur.amount))
      }, Big(0))
    )
    overpaid = totalBrokerPaid.gt(totalAmount)
      ? totalBrokerPaid.minus(totalAmount)
      : new Big(0)
    shortpaid = totalBrokerPaid.lt(totalAmount)
      ? totalAmount.minus(totalBrokerPaid)
      : new Big(0)
  }

  return {
    ...invoiceMappedForV2,
    amounts: {
      advance: invoiceMappedForV2.advance
        ? new Big(invoiceMappedForV2.advance)
        : new Big(0),
      client_total_to_pay_from_invoice: client_total_to_pay
        ? client_total_to_pay
        : new Big(0),
      detention: invoiceMappedForV2.detention
        ? new Big(invoiceMappedForV2.detention)
        : new Big(0),
      lumper: invoiceMappedForV2.lumper
        ? new Big(invoiceMappedForV2.lumper)
        : new Big(0),
      overpaid,
      primary_rate: new Big(invoice.lineHaulRate),
      shortpaid,
      total: totalAmount,
    },
    client_id: invoiceMappedForV2.client.id,
    // We only care about how many days since approved, not when an invoice was approved
    days_approved: daysApproved,
    debtor_id: invoiceMappedForV2.debtor?.id
      ? invoiceMappedForV2.debtor?.id
      : null,
    // Used on frontend to avoid transforming an already trasnformed invoice
    transformed: true,
    updates: invoiceMappedForV2.invoice_updates,
  }
}
